import { useMachine } from '@xstate/react';
import React, { createContext, useEffect } from 'react';

import {
  Context,
  ProductSelectedTrackingData,
} from 'src/modules/designStudio/machines/types';
import { useSplit } from 'src/shared/hooks';
import { identify } from 'src/shared/telemetry';

import { FeatureCategories } from '../../constants';
import { createMachine, initialContext } from '../../machines/history';

const defaultContext = {
  historyState: initialContext,
  updateDesign: () => undefined,
  resetDesign: () => undefined,
  undoDesignChange: () => undefined,
  redoDesignChange: () => undefined,
  canRedo: false,
  canUndo: false,
};

export type UpdateDesignFunction = ({
  category,
  update,
  trackingData,
}: UpdateDesignProps) => void;

interface UpdateDesignProps {
  category: FeatureCategories;
  update: {
    textureId?: number | null;
    color?: string | null;
    gridStyle?: string | null;
    frameStyle?: string | null;
  };
  trackingData: ProductSelectedTrackingData;
}

const context = createContext<{
  historyState: Context;
  updateDesign: (config: UpdateDesignProps) => void;
  resetDesign: () => void;
  undoDesignChange: () => void;
  redoDesignChange: () => void;
  canRedo: boolean;
  canUndo: boolean;
}>(defaultContext);

const Provider = ({
  jobId,
  children,
  authToken,
  shareToken,
}: {
  jobId: number;
  authToken?: string | null;
  shareToken?: string;
  children: React.ReactNode;
}) => {
  const {
    updateUserId: updateSplitUserId,
    updateAttributes: updateSplitAttributes,
  } = useSplit();
  const [machineState, send] = useMachine(
    createMachine({ jobId, authToken, shareToken }),
  );

  const { userId, email, user, canRedo, canUndo } = machineState.context;

  useEffect(() => {
    if (userId && email) {
      updateSplitUserId(userId.toString());
      updateSplitAttributes(user);
      void identify({ userId, email });
    }
  }, [userId, email, user, updateSplitUserId, updateSplitAttributes]);

  const resetDesign = () => {
    send({
      type: 'RESET_DESIGN',
    });
  };

  const updateDesign = ({
    category,
    update,
    trackingData,
  }: UpdateDesignProps) => {
    send({
      type: 'UPDATE_DESIGN',
      category,
      update,
      trackingData,
    });
  };

  const undoDesignChange = () => send({ type: 'UNDO_DESIGN_CHANGE' });
  const redoDesignChange = () => {
    send({ type: 'REDO_DESIGN_CHANGE' });
  };

  return (
    <context.Provider
      value={{
        historyState: machineState.context,
        updateDesign,
        resetDesign,
        undoDesignChange,
        redoDesignChange,
        canUndo,
        canRedo,
      }}
    >
      {children}
    </context.Provider>
  );
};

export { Provider, context };
